/**
 * ChatGPT Ads measurement pixel (`oaiq`).
 *
 * The SDK is injected by `gatsby-ssr.jsx` and only after Osano reports
 * MARKETING consent; the queue installed alongside it buffers calls made
 * before the script lands. Everything here no-ops when the pixel is disabled
 * (no `OPENAI_PIXEL_ID`) or the visitor blocks the script, so callers never
 * need to guard.
 */

declare global {
  interface Window {
    oaiq?: (...args: unknown[]) => void;
  }
}

/** Standard event names in OpenAI's taxonomy that we currently send. */
export type OpenAiAdsEvent = 'order_created';

export type OpenAiAdsContent = {
  id?: string;
  name?: string;
  /** Non-empty category such as `product`, `plan`, or `page`. */
  content_type?: string;
  quantity?: number;
  amount?: number;
  currency?: string;
};

/** Data shape shared by the commerce events (`order_created` among them). */
export type OpenAiAdsContentsData = {
  type: 'contents';
  amount?: number;
  currency?: string;
  contents?: OpenAiAdsContent[];
};

export type OpenAiAdsOptions = {
  /** Shared id that lets a later Conversions API leg dedupe against the pixel. */
  event_id?: string;
  opt_out?: boolean;
};

/**
 * Monetary values must be integers in the currency's minor unit: $25.99 is
 * sent as 2599.
 */
export const toMinorUnits = (amount: number): number =>
  Math.round(Number(amount) * 100);

export namespace openAiAds {
  export const measure = (
    event: OpenAiAdsEvent,
    data: OpenAiAdsContentsData,
    options?: OpenAiAdsOptions,
  ): void => {
    try {
      if (typeof window === 'undefined' || !window.oaiq) {
        return;
      }

      if (options) {
        window.oaiq('measure', event, data, options);
      } else {
        window.oaiq('measure', event, data);
      }
    } catch {
      // never let tracking break the page
    }
  };
}
